/**
 * Phase 550k — Watchdog Escalation Matrix configuration modal.
 *
 * Lets the Super Admin set a phone number per role so each anomaly
 * tripwire routes to the team that owns the fix:
 *   • marketing_ops      → BOUNCE_RATE_HIGH
 *   • engineering_oncall → CLOSER_DRIFT (LLM hallucination)
 *   • devops_infra       → AE_CRON_SILENT (worker dead)
 *   • super_admin_master → SDR_BACKLOG (and fallback for empty roles)
 *
 * Wires:
 *   GET  /super-admin/sales-team/anomaly-watchdog/escalation-matrix
 *   POST .../escalation-matrix
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Megaphone, Code2, Server, Crown, AlertTriangle, ShieldCheck,
} from 'lucide-react';

const ROLE_META = {
  marketing_ops: {
    label: 'Marketing Ops',
    icon: Megaphone,
    tone: 'text-amber-700 border-amber-200 bg-amber-50/60',
    routedFor: 'BOUNCE_RATE_HIGH',
    rationale: 'Domain reputation issues — rotate sender domains',
  },
  engineering_oncall: {
    label: 'Engineering On-Call',
    icon: Code2,
    tone: 'text-cyan-700 border-cyan-200 bg-cyan-50/60',
    routedFor: 'CLOSER_DRIFT',
    rationale: 'LLM classifier drift — tune INTENT_TEMPLATES',
  },
  devops_infra: {
    label: 'DevOps / Infra',
    icon: Server,
    tone: 'text-emerald-700 border-emerald-200 bg-emerald-50/60',
    routedFor: 'AE_CRON_SILENT',
    rationale: 'Cron worker failure — restart APScheduler',
  },
  super_admin_master: {
    label: 'Super Admin (Master + Fallback)',
    icon: Crown,
    tone: 'text-fuchsia-700 border-fuchsia-200 bg-fuchsia-500/5',
    routedFor: 'SDR_BACKLOG',
    rationale: 'Sales velocity + fallback when any role is unset',
  },
};

const ROLE_ORDER = [
  'super_admin_master',
  'devops_infra',
  'engineering_oncall',
  'marketing_ops',
];

const isValidPhoneOrEmpty = (s) =>
  s === '' || /^\+\d{8,15}$/.test(s);

export default function SOSConfigModal({ open, onOpenChange, onSaved }) {
  const [matrix, setMatrix] = useState({
    super_admin_master: '',
    devops_infra: '',
    engineering_oncall: '',
    marketing_ops: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [legacyPhone, setLegacyPhone] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get('/super-admin/sales-team/anomaly-watchdog/escalation-matrix')
      .then((resp) => {
        setMatrix({ ...matrix, ...(resp.data?.matrix || {}) });
        setLegacyPhone(resp.data?.legacy_master_phone || '');
      })
      .catch((e) =>
        toast.error(e?.response?.data?.detail || 'Failed to load matrix'),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setRole = (role, value) =>
    setMatrix((m) => ({ ...m, [role]: value }));

  const handleSave = async () => {
    // Client-side validation mirrors the backend's
    for (const role of ROLE_ORDER) {
      const phone = matrix[role] || '';
      if (!isValidPhoneOrEmpty(phone)) {
        toast.error(
          `${ROLE_META[role].label}: must be E.164 (e.g. +919876543210) or empty`,
        );
        return;
      }
    }
    setSaving(true);
    try {
      await api.post(
        '/super-admin/sales-team/anomaly-watchdog/escalation-matrix',
        { matrix },
      );
      toast.success('Escalation Matrix saved — next watchdog sweep uses it.');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl border-gray-100 bg-white text-gray-900"
        data-testid="sos-config-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Watchdog Escalation Matrix
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Route each Anomaly Watchdog tripwire to the team best
            equipped to fix it. Empty fields automatically fall back
            to the Super Admin (Master) number.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full bg-gray-100" />
            ))}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            {legacyPhone && !matrix.super_admin_master && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2 text-[11px] text-amber-700">
                Legacy SOS phone detected:{' '}
                <span className="font-mono">{legacyPhone}</span>. Save
                to migrate it into the Master slot.
              </div>
            )}

            {ROLE_ORDER.map((role) => {
              const meta = ROLE_META[role];
              const Icon = meta.icon;
              return (
                <div
                  key={role}
                  className={`rounded-lg border p-3 ${meta.tone}`}
                  data-testid={`sos-role-row-${role}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1 rounded-md bg-gray-50 p-1.5">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium text-gray-900">
                          {meta.label}
                        </span>
                        <Badge
                          variant="outline"
                          className="border-gray-200 bg-white text-[10px] text-gray-500"
                        >
                          routes: {meta.routedFor}
                        </Badge>
                      </div>
                      <p className="mt-0.5 text-[11px] text-gray-500">
                        {meta.rationale}
                      </p>
                      <Input
                        value={matrix[role] || ''}
                        onChange={(e) => setRole(role, e.target.value.trim())}
                        placeholder="+919876543210"
                        className="mt-2 border-gray-200 bg-white font-mono text-sm text-gray-900 placeholder:text-gray-300"
                        data-testid={`sos-input-${role}`}
                      />
                      {matrix[role] && !isValidPhoneOrEmpty(matrix[role]) && (
                        <p className="mt-1 flex items-center gap-1 text-[10px] text-rose-700">
                          <AlertTriangle className="h-3 w-3" />
                          E.164 format required (e.g. +919876543210)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
            data-testid="sos-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-emerald-600 text-white hover:bg-emerald-500"
            data-testid="sos-save-btn"
          >
            {saving ? 'Saving…' : 'Save Matrix'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
